import React, { useRef, useCallback, useImperativeHandle } from 'react';
import { DatePickerInputProps, PickerVariant } from '../types';
import { useFormatDate } from '../hooks';
import { useDisclosure } from '../../../hooks';
import { nullFn } from '../utils';
import { DatePickerInputModal } from './DatePickerModal';
import EventsIcon from '../../../../assets/images/svg/EventsIcon';
import { classNames } from '../../../utils';
import CloseButton from '../../button/components/CloseButton';

const VARIANT_MAP: Record<PickerVariant, string> = {
  [PickerVariant.BASIC]:
    'rounded-lg w-full border font-medium text-base border-gray-light shadow-sm px-16 pr-5 py-4 appearance-none placeholder-gray-light focus:outline-none focus:ring-2 focus:ring-green focus:border-transparent',
  [PickerVariant.DROPDOWN]:
    'relative pl-4 pr-2 py-1.5 w-full flex flex-row items-center justify-between outline-none focus:outline-none border-2 border-gray-light rounded-lg',
};

function DatePickerInput({
  label,
  name,
  locale = 'en-US',
  className = '',
  formatStyle = 'medium',
  maxValue = undefined,
  minValue = undefined,
  onChange = nullFn,
  placeholder = '',
  ref = null,
  style = undefined,
  value = undefined,
  disabled = false,
  required = false,
  readOnly = false,
  onClick = nullFn,
  onFocus = nullFn,
  onBlur = nullFn,
  selectionType = 'single',
  variant = 'single',
  error = null,
  id = undefined,
  modalClassName = '',
  pickerVariant = 'BASIC',
}: DatePickerInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const formattedDate = useFormatDate({
    value,
    format: formatStyle,
    locale: locale,
    selectionType: selectionType,
  });

  const { isOpen, open: openModal, close: closeModal } = useDisclosure(false);
  const modalId = id && `${id}_modal`;

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    click: () => {
      if (inputRef.current) {
        inputRef.current.click();
      }
    },
    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
  }));

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!readOnly) {
        openModal();
        onClick(event);
      }
    },
    [onClick, openModal, readOnly],
  );

  const handleFocus = () => {
    onFocus(value);
  };

  const handleBlur = () => {
    onBlur(value);
  };

  const handleChange = useCallback(
    (newValue: Date | Date[] | undefined) => {
      if (
        selectionType === 'single' ||
        (Array.isArray(newValue) && newValue.length > 1)
      ) {
        closeModal();
      }
      onChange(newValue);
    },
    [closeModal, onChange, selectionType],
  );

  const handleClearValue = useCallback(() => {
    if (selectionType === 'single') {
      closeModal();
    }
    onChange(undefined);
  }, [closeModal, onChange, selectionType]);

  return (
    <div>
      <div
        className={`flex w-auto flex-col ${className}`}
        style={style}
        id={id}
      >
        {label && (
          <div className='mb-2 flex flex-row items-center justify-between'>
            <label
              className='text-base font-medium tracking-tight text-gray-dark'
              htmlFor={name}
            >
              {label}
            </label>
          </div>
        )}

        {/* INPUT HERE */}
        <div className='relative w-full'>
          <input
            ref={inputRef}
            id={name}
            name={name}
            placeholder={placeholder}
            value={formattedDate}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            required={required}
            disabled={disabled}
            readOnly
            type='text'
            autoComplete='off'
            className={classNames(VARIANT_MAP[PickerVariant[pickerVariant]])}
          />
          {pickerVariant !== 'DROPDOWN' && (
            <EventsIcon
              width='26px'
              height='26px'
              className='absolute left-0 top-1/2 ml-5 -translate-y-1/2 transform stroke-green'
            />
          )}
          {value !== undefined ? (
            <CloseButton
              onClick={handleClearValue}
              className='absolute right-0 top-1/2 mr-5 -translate-y-1/2 transform'
            />
          ) : null}
        </div>
      </div>
      {error ? error : null}
      {/* MODAL HERE */}
      <DatePickerInputModal
        id={modalId}
        isOpen={isOpen}
        onClose={closeModal}
        locale={locale}
        selectionType={selectionType}
        value={value}
        onChange={handleChange}
        maxValue={maxValue}
        minValue={minValue}
        variant={variant}
        className={modalClassName}
      />
    </div>
  );
}

export default DatePickerInput;
